import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  BILLING_KNOWN, IDENTITY_ROLE, claimsOfIdToken, hasPortalClaim, identityRole,
} from '../domain/identity';
import { watchBillingPresence } from '../data/identity';

// Is the signed-in uid a studio, a family's portal login, or both (#764) —
// the web's half of the core's decision (core/src/identity.rs).
//
// The signal is the `portal` claim in the ID token the browser already holds,
// read out of the token itself (`claimsOfIdToken`): synchronous, no network,
// offline included. A teacher's token has no such claim, so the answer is
// `studio` on the first render, without any read, and the studio's providers
// mount at once. Only a token with the claim reads anything: whether the uid
// also has a billing account, which makes it a studio too (`both`); until that
// is known the answer is `pending`, because guessing `studio` is the guess
// that would write.
//
// A user object whose token cannot be read here (a test double) is asked once
// through `getIdTokenResult`, and is a studio meanwhile and on any failure.
//
// Signed out, still loading, or with no Firebase configured, it is `studio`;
// the router and RequireAuth answer those.
export function useIdentityRole() {
  const { user, loading, isConfigured } = useAuth();
  const uid = user?.uid ?? null;
  const tokenClaims = user ? claimsOfIdToken(user.accessToken) : null;

  const [asked, setAsked] = useState({ uid: null, claim: false });
  const [billing, setBilling] = useState({ uid: null, known: BILLING_KNOWN.unknown });

  const readable = tokenClaims != null;
  useEffect(() => {
    if (!user || readable || typeof user.getIdTokenResult !== 'function') return undefined;
    let live = true;
    user.getIdTokenResult().then(
      (result) => { if (live) setAsked({ uid: user.uid, claim: hasPortalClaim(result?.claims) }); },
      () => { if (live) setAsked({ uid: user.uid, claim: false }); },
    );
    return () => { live = false; };
  }, [user, readable]);

  const claim = !user ? false
    : readable ? hasPortalClaim(tokenClaims)
      : (asked.uid === uid && asked.claim);

  useEffect(() => {
    if (!uid || !claim) return undefined;
    return watchBillingPresence(uid, (known) => setBilling({ uid, known }));
  }, [uid, claim]);

  if (!isConfigured || loading || !user || !claim) return IDENTITY_ROLE.studio;
  return identityRole({
    portalClaim: true,
    billing: billing.uid === uid ? billing.known : BILLING_KNOWN.unknown,
  });
}
